
import { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';

interface CollaborationContextStepProps {
  updateFormData?: (data: any) => void;
  formData?: any;
  initialStartDate?: string;
  // Nouvelles props pour le pré-remplissage depuis les données candidat
  candidateStartDate?: string;
  candidateEndDate?: string;
  candidateStillWorkingTogether?: boolean;
}

export const CollaborationContextStep = ({ 
  updateFormData, 
  formData, 
  initialStartDate = '',
  candidateStartDate,
  candidateEndDate,
  candidateStillWorkingTogether = false
}: CollaborationContextStepProps) => {
  // Pré-remplissage intelligent : candidat > formData > initialStartDate
  const [collaborationStartDate, setCollaborationStartDate] = useState(
    candidateStartDate || formData?.collaborationStartDate || initialStartDate
  );
  const [collaborationEndDate, setCollaborationEndDate] = useState(
    candidateEndDate || formData?.collaborationEndDate || ''
  );
  const [stillWorkingTogether, setStillWorkingTogether] = useState(
    candidateStillWorkingTogether || formData?.stillWorkingTogether || false
  );
  const [error, setError] = useState('');
  
  // Refs pour accéder aux inputs de date
  const startDateInputRef = useRef<HTMLInputElement>(null);
  const endDateInputRef = useRef<HTMLInputElement>(null);

  // S'assurer que le formData est toujours synchronisé avec l'état local
  useEffect(() => {
    if (updateFormData) {
      // Utiliser la structure attendue par la validation
      updateFormData({
        collaborationStartDate: collaborationStartDate,
        collaborationEndDate: stillWorkingTogether ? '' : collaborationEndDate,
        stillWorkingTogether: stillWorkingTogether
      });
    }
  }, [collaborationStartDate, collaborationEndDate, stillWorkingTogether, updateFormData]);

  // Mettre à jour formData quand les dates changent
  const handleDateChange = (field: string, value: string) => {
    if (field === 'start') {
      setCollaborationStartDate(value);
    } else {
      setCollaborationEndDate(value);
    }

    // Validation: date de fin ne peut pas être antérieure à la date de début
    const startDate = field === 'start' ? value : collaborationStartDate;
    const endDate = field === 'end' ? value : collaborationEndDate;
    
    // Vérifier si la date de fin est antérieure à la date de début
    if (endDate && startDate && endDate < startDate && !stillWorkingTogether) {
      setError('La date de fin de collaboration ne peut pas être antérieure à la date de début.');
      // Ne pas mettre à jour le formData avec une date invalide
      return;
    } else {
      setError('');
    }

    // Mettre à jour les données du formulaire seulement si les dates sont valides
    if (updateFormData) {
      updateFormData({
        collaborationStartDate: startDate,
        collaborationEndDate: stillWorkingTogether ? '' : endDate,
        stillWorkingTogether: stillWorkingTogether
      });
    }
  };

  // Gérer le changement de la checkbox "Nous travaillons toujours ensemble"
  const handleStillWorkingChange = (checked: boolean) => {
    setStillWorkingTogether(checked);
    if (checked) {
      // Si cochée, vider la date de fin
      setCollaborationEndDate('');
      setError('');
    }
  };

  // Gestionnaires de clic pour les icônes SVG
  const handleStartDateIconClick = () => {
    if (startDateInputRef.current) {
      startDateInputRef.current.showPicker?.();
    }
  };

  const handleEndDateIconClick = () => {
    if (endDateInputRef.current) {
      endDateInputRef.current.showPicker?.();
    }
  };

  return (
    <div className="space-y-6 font-['Bricolage_Grotesque']">
      <div className="text-center">
        <p className="text-gray-600 text-base font-normal font-['Bricolage_Grotesque']">
          Précisez les dates de début et de fin de votre collaboration.
        </p>
      </div>

      <div className="w-full flex flex-col justify-start items-start gap-6">
        {/* Date de début */}
        <div className="w-full flex flex-col justify-start items-start gap-3">
          <div className="self-stretch justify-center text-gray-800 text-sm font-medium font-['Bricolage_Grotesque']">
            Date de début de la collaboration
          </div>
          <div className="self-stretch h-12 px-3 py-3.5 bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 inline-flex justify-between items-center">
            <Input
              ref={startDateInputRef}
              id="collaborationStartDate"
              type="date"
              value={collaborationStartDate}
              onChange={(e) => handleDateChange('start', e.target.value)}
              className="border-0 outline-0 p-0 text-sm font-normal font-['Bricolage_Grotesque'] bg-transparent flex-1 w-full [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-inner-spin-button]:hidden [&::-webkit-outer-spin-button]:hidden"
              placeholder="Sélectionnez une date"
              required
            />
            <div className="relative cursor-pointer" onClick={handleStartDateIconClick}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5.33325 1.33301V3.33301" stroke="#8F949D" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10.6667 1.33301V3.33301" stroke="#8F949D" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2.33325 6.05957H13.6666" stroke="#8F949D" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 5.66634V11.333C14 13.333 13 14.6663 10.6667 14.6663H5.33333C3 14.6663 2 13.333 2 11.333V5.66634C2 3.66634 3 2.33301 5.33333 2.33301H10.6667C13 2.33301 14 3.66634 14 5.66634Z" stroke="#8F949D" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10.4632 9.13314H10.4692" stroke="#8F949D" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10.4632 11.1331H10.4692" stroke="#8F949D" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M7.99691 9.13314H8.0029" stroke="#8F949D" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M7.99691 11.1331H8.0029" stroke="#8F949D" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M5.52962 9.13314H5.53561" stroke="#8F949D" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M5.52962 11.1331H5.53561" stroke="#8F949D" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
        </div>

        {/* Checkbox "Nous travaillons toujours ensemble" */}
        <div className="w-full flex items-center gap-3">
          <input
            type="checkbox"
            id="stillWorkingTogether"
            checked={stillWorkingTogether}
            onChange={(e) => handleStillWorkingChange(e.target.checked)}
            className="w-4 h-4 text-teal-600 bg-gray-100 border-gray-300 rounded focus:ring-teal-500 focus:ring-2"
          />
          <label 
            htmlFor="stillWorkingTogether"
            className="text-sm font-normal font-['Bricolage_Grotesque'] text-gray-700 cursor-pointer"
          >
            Nous travaillons toujours ensemble
          </label>
        </div>

        {/* Date de fin (conditionnelle) */}
        {!stillWorkingTogether && (
          <div className="w-full flex flex-col justify-start items-start gap-3">
            <div className="self-stretch justify-center text-gray-800 text-sm font-medium font-['Bricolage_Grotesque']">
              Date de fin de la collaboration
            </div>
            <div className="self-stretch h-12 px-3 py-3.5 bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 inline-flex justify-between items-center">
              <Input
                ref={endDateInputRef}
                id="collaborationEndDate"
                type="date"
                value={collaborationEndDate}
                onChange={(e) => handleDateChange('end', e.target.value)}
                className="border-0 outline-0 p-0 text-sm font-normal font-['Bricolage_Grotesque'] bg-transparent flex-1 w-full [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-inner-spin-button]:hidden [&::-webkit-outer-spin-button]:hidden"
                placeholder="Sélectionnez une date"
                min={collaborationStartDate}
              />
              <div className="relative cursor-pointer" onClick={handleEndDateIconClick}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5.33325 1.33301V3.33301" stroke="#8F949D" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M10.6667 1.33301V3.33301" stroke="#8F949D" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2.33325 6.05957H13.6666" stroke="#8F949D" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 5.66634V11.333C14 13.333 13 14.6663 10.6667 14.6663H5.33333C3 14.6663 2 13.333 2 11.333V5.66634C2 3.66634 3 2.33301 5.33333 2.33301H10.6667C13 2.33301 14 3.66634 14 5.66634Z" stroke="#8F949D" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M10.4632 9.13314H10.4692" stroke="#8F949D" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M10.4632 11.1331H10.4692" stroke="#8F949D" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M7.99691 9.13314H8.0029" stroke="#8F949D" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M7.99691 11.1331H8.0029" stroke="#8F949D" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5.52962 9.13314H5.53561" stroke="#8F949D" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5.52962 11.1331H5.53561" stroke="#8F949D" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="text-red-600 text-sm font-normal font-['Bricolage_Grotesque']">{error}</div>
        )}
      </div>
    </div>
  );
};
